/**
 * "■ Kết thúc hôm nay" ở thanh trên cùng (`openReport` của bản mẫu): chưa đủ mục tiêu thì hỏi lại,
 * đủ rồi thì chốt ngày và hiện tổng kết. Mở bằng sự kiện `ca-day-end`.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ActivityReport, DaySummary, type Account } from "@ca/shared";
import { Button, Chip, Fish, Kpi, Modal, SpaceTag } from "@ca/ui";
import { api } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { studyQueue } from "./study-tracker.ts";
import { ShareButton } from "./community.tsx";
import { useToday } from "./productivity.tsx";
import { useFocus } from "./focus.tsx";
import { plusDay } from "./planner.tsx";
import { NewBadge } from "./flags.tsx";
import {
  HBars,
  Legend,
  StackedCols,
  fmtMin,
  presentActs,
  sum,
  tipFor,
  type Col,
  type Parts,
} from "./charts.tsx";

export const WEEKDAYS = [
  "Chủ Nhật",
  "Thứ Hai",
  "Thứ Ba",
  "Thứ Tư",
  "Thứ Năm",
  "Thứ Sáu",
  "Thứ Bảy",
];
/** "Thứ Năm 01/10" từ ngày ISO. */
export function dayLabel(date: string) {
  const d = new Date(`${date}T00:00:00`);
  return `${WEEKDAYS[d.getDay()] ?? ""} ${date.slice(8, 10)}/${date.slice(5, 7)}`;
}
export function useActivity(uid: string, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ["activity", uid, from, to],
    queryFn: async () =>
      ActivityReport.parse(await api(`/v1/me/activity?from=${from}&to=${to}`, { accountId: uid })),
    enabled,
    refetchInterval: 30000,
  });
}
/** Phút học theo giờ (6h–23h) và theo nhóm việc của một ngày. */
export function hourCols(report: ActivityReport | undefined) {
  const hourly: Record<number, Parts> = {};
  for (const b of report?.buckets ?? []) {
    const h = (hourly[b.hour] ??= {});
    h[b.activity] = (h[b.activity] ?? 0) + b.activeSecs / 60;
  }
  const cols: Col[] = Array.from({ length: 18 }, (_, k) => {
    const h = k + 6,
      parts = hourly[h] ?? {};
    return {
      label: `${String(h).padStart(2, "0")}h`,
      parts,
      tip: tipFor(`${h}:00–${h + 1}:00`, parts),
    };
  });
  let peak = 0;
  cols.forEach((c, i) => {
    if (sum(c.parts) > sum(cols[peak]?.parts ?? {})) peak = i;
  });
  const top = cols[peak];
  if (top && sum(top.parts)) top.val = `${Math.round(sum(top.parts))}p`;
  return cols;
}
export function activityTotals(report: ActivityReport | undefined) {
  const totals: Parts = {};
  for (const b of report?.buckets ?? [])
    totals[b.activity] = (totals[b.activity] ?? 0) + b.activeSecs / 60;
  return totals;
}

export function DayEnd({ account, notify }: { account: Account; notify: (s: string) => void }) {
  const uid = account.id,
    today = useToday(uid),
    focus = useFocus(uid),
    cache = useQueryClient(),
    navigate = useNavigate();
  const [ask, setAsk] = useState(false),
    [summary, setSummary] = useState<DaySummary | null>(null),
    [busy, setBusy] = useState(false),
    [added, setAdded] = useState(false);
  const t = today.data;
  const report = useActivity(uid, summary?.date ?? "", summary?.date ?? "", !!summary);
  const close = async () => {
    if (!t) return;
    setBusy(true);
    try {
      await studyQueue.drain(uid);
      const snapshot = DaySummary.parse(
        await api(`/v1/me/day-summaries/${t.date}`, { method: "PUT", accountId: uid }),
      );
      setAsk(false);
      setAdded(false);
      setSummary(snapshot);
      await today.refetch();
    } catch (e) {
      notify(String(e));
    } finally {
      setBusy(false);
    }
  };
  // Bấm "Kết thúc hôm nay" lúc dữ liệu Hôm nay chưa tải xong thì nhớ lại và mở khi có dữ liệu.
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    const open = () => {
      setRequested(true);
    };
    window.addEventListener("ca-day-end", open);
    return () => {
      window.removeEventListener("ca-day-end", open);
    };
  }, []);
  useEffect(() => {
    if (!requested || !t) return;
    setRequested(false);
    if (t.activeSecs < t.goalMinutes * 60) setAsk(true);
    else void close();
  }, [requested, t]);
  const minutes = Math.floor((t?.activeSecs ?? 0) / 60);
  const left = Math.max(0, (t?.goalMinutes ?? 0) - minutes);
  const addTomorrow = async (s: DaySummary) => {
    const [h = 20, m = 0] = (account.profile.studySlot?.from ?? "20:00").split(":").map(Number);
    const start = Math.floor((h * 60 + m) / 15) * 15;
    try {
      await api(`/v1/me/calendar/${uuidv7()}`, {
        method: "PUT",
        accountId: uid,
        key: uuidv7(),
        body: {
          event: {
            date: plusDay(s.date, 1),
            startMin: start,
            endMin: Math.min(1440, start + 30),
            title: `Part ${s.nextPart} · 30 phút`,
            skill: s.nextPart <= 4 ? "nghe" : "doc",
            done: false,
          },
          expectedUpdatedAt: null,
        },
      });
      setAdded(true);
      await cache.invalidateQueries({ queryKey: ["calendar", uid] });
    } catch (e) {
      notify(String(e));
    }
  };
  return (
    <>
      {ask && t && (
        <Modal
          title="Kết thúc hôm nay?"
          onClose={() => {
            setAsk(false);
          }}
        >
          <div className="rep-hero">
            <div>
              <Fish />
            </div>
            <div>
              <h2>Hôm nay bạn mới học {fmtMin(minutes)}</h2>
              <p>
                Còn {left} phút nữa là đạt mục tiêu ngày
                {t.streak ? ` và giữ chuỗi ${t.streak} ngày` : ""}. Một hiệp 25 phút là gần đủ.
              </p>
            </div>
          </div>
          <div className="row" style={{ marginTop: 16 }}>
            <Button
              size="lg"
              onClick={() => {
                setAsk(false);
                window.dispatchEvent(new Event("ca-focus-start"));
                window.dispatchEvent(new Event("ca-focus-open"));
              }}
            >
              ▶ Học thêm 1 hiệp 25 phút
            </Button>
            <Button
              variant="soft"
              size="lg"
              disabled={busy}
              onClick={() => {
                void close();
              }}
            >
              {busy ? "Đang tổng kết…" : "Vẫn kết thúc"}
            </Button>
          </div>
        </Modal>
      )}
      {summary && (
        <Modal
          title="Tổng kết hôm nay"
          head={
            <div className="row">
              <SpaceTag space="prod" />
              <NewBadge />
            </div>
          }
          wide
          onClose={() => {
            setSummary(null);
          }}
        >
          <SummaryBody
            summary={summary}
            streak={t?.streak ?? 0}
            rounds={focus.data?.rounds.length ?? 0}
            habits={t?.habits ?? []}
            report={report.data}
          />
          <div className="tomorrow" style={{ marginTop: 14 }}>
            <div className="row between">
              <h3 style={{ fontSize: 16 }}>
                🗓️ Ngày mai học gì? <NewBadge />
              </h3>
              <Chip>gợi ý từ Bảng điểm</Chip>
            </div>
            <p className="muted" style={{ fontSize: 13.5 }}>
              {summary.tomorrow}
            </p>
            <div className="row">
              <Chip tone="dark">{dayLabel(plusDay(summary.date, 1))}</Chip>
              <Chip>
                {summary.nextPart <= 4 ? "🎧" : "📖"} Part {summary.nextPart} · 30 phút
              </Chip>
              {added ? (
                <Chip tone="good">✓ Đã thêm vào lịch</Chip>
              ) : (
                <Button
                  variant="acc"
                  size="sm"
                  onClick={() => {
                    void addTomorrow(summary);
                  }}
                >
                  ＋ Thêm vào lịch
                </Button>
              )}
              <Button
                variant="soft"
                size="sm"
                onClick={() => {
                  setSummary(null);
                  void navigate({
                    to: "/$",
                    params: {
                      _splat: `practice/${summary.nextPart <= 4 ? "listening" : "reading"}/${summary.nextPart}`,
                    },
                  });
                }}
              >
                Mở phần luyện được gợi ý
              </Button>
            </div>
          </div>
          <div className="row" style={{ marginTop: 16, justifyContent: "space-between" }}>
            <ShareButton uid={uid} notify={notify} input={{ kind: "day", date: summary.date }} />
            <Button
              size="lg"
              onClick={() => {
                setSummary(null);
              }}
            >
              Tạm biệt, hẹn mai 👋
            </Button>
          </div>
          <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>
            Bạn vẫn có thể học tiếp hôm nay. Tổng kết sẽ cập nhật khi các nhịp offline được đồng bộ.
          </p>
        </Modal>
      )}
    </>
  );
}

function SummaryBody({
  summary,
  streak,
  rounds,
  habits,
  report,
}: {
  summary: DaySummary;
  streak: number;
  rounds: number;
  habits: { id: string; name: string; checked: boolean }[];
  report: ActivityReport | undefined;
}) {
  const minutes = Math.floor(summary.activeSecs / 60),
    over = minutes - summary.goalMinutes,
    acc = summary.answered ? Math.round((summary.correct / summary.answered) * 100) : 0,
    totals: Parts = {};
  for (const a of summary.byActivity) totals[a.activity] = a.activeSecs / 60;
  const cols = hourCols(report);
  return (
    <>
      <div className="rep-hero">
        <div>
          <Fish pose={over >= 0 ? "party" : undefined} />
        </div>
        <div>
          <span className="eyebrow" style={{ color: "inherit", opacity: 0.7 }}>
            Tổng kết hôm nay · {dayLabel(summary.date)}
          </span>
          <h2>
            Hôm nay bạn học {fmtMin(minutes)}
            {over >= 0 ? " 🎉" : ""}
          </h2>
          <p>
            {over >= 0
              ? `Vượt mục tiêu ${over} phút`
              : `Còn ${-over} phút nữa là đạt mục tiêu ${summary.goalMinutes} phút`}
            {streak ? ` · chuỗi ${streak} ngày 🔥` : ""}
          </p>
        </div>
      </div>
      <div className="kpis" style={{ marginTop: 14 }}>
        <Kpi label="Hiệp tập trung" value={`${rounds} 🍅`} />
        <Kpi label="Câu đã làm" value={summary.answered} sub={`đúng ${acc}%`} />
        <Kpi label="Từ vựng" value={summary.reviewedWords} sub={`+${summary.newWords} từ mới`} />
        <Kpi label="Lỗi đã trị" value={`${summary.fixedGroups.length} nhóm`} />
      </div>
      <div className="card tight" style={{ background: "var(--surface-2)", marginTop: 14 }}>
        <div className="row between" style={{ marginBottom: 6 }}>
          <b>Bạn học lúc nào?</b>
          <span className="faint" style={{ fontSize: 12, fontWeight: 700 }}>
            phút theo giờ
          </span>
        </div>
        <Legend ids={presentActs(cols.map((c) => c.parts))} />
        <div className="chart-scroll">
          <div className="chart">
            <StackedCols
              cols={cols}
              W={760}
              H={200}
              step={20}
              labelEvery={2}
              aria="Phút học theo giờ hôm nay"
            />
          </div>
        </div>
      </div>
      <div className="grid g2" style={{ marginTop: 14 }}>
        <div>
          <b>Hôm nay học gì?</b>
          <div style={{ marginTop: 6 }}>
            {sum(totals) ? (
              <HBars totals={totals} />
            ) : (
              <p className="faint">Chưa có phút học nào.</p>
            )}
          </div>
        </div>
        <div className="stack" style={{ gap: 8 }}>
          <b>Thói quen tự tick</b>
          <div className="habit-chips">
            {habits.length ? (
              habits.map((h) => (
                <Chip key={h.id} tone={h.checked ? "good" : undefined}>
                  {h.checked ? "✓" : "○"} {h.name}
                </Chip>
              ))
            ) : (
              <span className="faint">Chưa có thói quen.</span>
            )}
          </div>
          <p className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
            Không phải tick tay: Cá tick từ những gì bạn làm thật.
          </p>
        </div>
      </div>
    </>
  );
}
